import { useState, useEffect, useMemo } from 'react';
import {
  X, Euro, Calendar, Wallet, ChevronLeft, ChevronRight,
  CheckSquare, Square, AlertCircle, Briefcase, ArrowRight,
  Check, Info
} from 'lucide-react';
import { formatCurrency } from '../utils/formatters';
import {
  getSalaryPeriodTransactions,
  computeSalaryCoefficient,
  computeRescaledAmounts,
} from '../features/debts/salaryLogic';
import { extractMoneyDigits, formatMoneyInput, moneyDigitsToNumber } from '../utils/moneyInput';
import MobileAmountKeypad from './MobileAmountKeypad';
import useIsMobileViewport from '../hooks/useIsMobileViewport';
import CustomSelect from './CustomSelect';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

// ────────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────────
const todayISO = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const firstDayOfCurrentMonthISO = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};

const formatDateLabel = (isoDate) => {
  try {
    return format(parseISO(isoDate), 'd MMM yyyy', { locale: it });
  } catch {
    return isoDate;
  }
};

const shiftDate = (isoDate, days) => {
  if (!isoDate) return isoDate;
  const [year, month, day] = isoDate.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ────────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────────
const SalaryWizard = ({
  personName,
  incomes = [],
  wallets = [],
  onClose,
  onApply,       // ({ realAmount, walletId, selectedIds, rescaledAmounts, closingDebt, coefficient })
}) => {
  const isMobileViewport = useIsMobileViewport();

  // ── Step state ──────────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // ── Step 1 form ─────────────────────────────────────────────────────────────
  const [amountDigits, setAmountDigits] = useState('');
  const [showMobileKeypad, setShowMobileKeypad] = useState(false);
  const [walletId, setWalletId] = useState(wallets[0]?.id ?? '');
  const [startDate, setStartDate] = useState(firstDayOfCurrentMonthISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [step1Errors, setStep1Errors] = useState({});

  // ── Step 2: transaction selection ───────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ── Derived: period transactions ────────────────────────────────────────────
  const periodTransactions = useMemo(
    () => getSalaryPeriodTransactions(incomes, personName, startDate, endDate),
    [incomes, personName, startDate, endDate]
  );

  // Auto-select all when step 2 opens / transactions change
  useEffect(() => {
    if (step === 2) {
      setSelectedIds(new Set(periodTransactions.map((tx) => tx.id)));
    }
  }, [step, periodTransactions]);

  // ── Derived calculations ─────────────────────────────────────────────────────
  const realAmount = moneyDigitsToNumber(amountDigits);

  const selectedTransactions = useMemo(
    () => periodTransactions.filter((tx) => selectedIds.has(tx.id)),
    [periodTransactions, selectedIds]
  );

  const selectedTotal = useMemo(
    () => selectedTransactions.reduce((sum, tx) => sum + tx.amount, 0),
    [selectedTransactions]
  );

  const coefficient = useMemo(
    () => computeSalaryCoefficient(realAmount, selectedTotal),
    [realAmount, selectedTotal]
  );

  const rescaledAmounts = useMemo(
    () => computeRescaledAmounts(selectedTransactions, realAmount, coefficient),
    [selectedTransactions, realAmount, coefficient]
  );

  // Build a map id→newAmount for display
  const rescaledMap = useMemo(() => {
    const map = {};
    rescaledAmounts.forEach(({ id, newAmount }) => { map[id] = newAmount; });
    return map;
  }, [rescaledAmounts]);

  // ── Wallet options ───────────────────────────────────────────────────────────
  const walletOptions = useMemo(
    () => wallets.map((w) => ({
      value: w.id,
      label: w.name,
      icon: (
        <div
          className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
          style={{ backgroundColor: w.color === 'rainbow' ? '#6366f1' : w.color }}
        />
      ),
    })),
    [wallets]
  );

  // ── Validation Step 1 ────────────────────────────────────────────────────────
  const validateStep1 = () => {
    const errors = {};
    if (!realAmount || realAmount <= 0) errors.amount = 'Inserisci un importo valido';
    if (!walletId) errors.walletId = 'Seleziona un conto';
    if (!startDate) errors.startDate = 'Data inizio obbligatoria';
    if (!endDate) errors.endDate = 'Data fine obbligatoria';
    if (startDate && endDate && startDate > endDate) {
      errors.endDate = 'La data fine deve essere successiva alla data inizio';
    }
    setStep1Errors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleGoToStep2 = () => {
    if (validateStep1()) setStep(2);
  };

  // ── Selection toggles ────────────────────────────────────────────────────────
  const toggleTransaction = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === periodTransactions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(periodTransactions.map((tx) => tx.id)));
    }
  };

  // ── Apply ────────────────────────────────────────────────────────────────────
  const handleApply = () => {
    if (selectedIds.size === 0) return;
    onApply({
      realAmount,
      walletId,
      selectedIds,
      rescaledAmounts,
      coefficient,
    });
  };

  // ── Date shift helpers ───────────────────────────────────────────────────────
  const shiftStart = (days) => setStartDate((d) => shiftDate(d, days));
  const shiftEnd = (days) => setEndDate((d) => shiftDate(d, days));

  // ────────────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
        onClick={(e) => {
          e.stopPropagation();
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-md animate-fade-in-up flex flex-col max-h-[92vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Header ──────────────────────────────────────────────────────── */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-4 rounded-t-3xl flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                <h2 className="text-lg font-bold">Stipendio Ricevuto</h2>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-3">
              {[1, 2].map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      step === s
                        ? 'bg-white text-emerald-700 shadow-lg'
                        : step > s
                        ? 'bg-emerald-400/60 text-white'
                        : 'bg-white/20 text-white/60'
                    }`}
                  >
                    {step > s ? <Check className="w-3 h-3" /> : s}
                  </div>
                  <span className={`text-xs font-medium ${step >= s ? 'text-white' : 'text-white/40'}`}>
                    {s === 1 ? 'Dettagli' : 'Transazioni'}
                  </span>
                  {s < 2 && <ChevronRight className="w-3 h-3 text-white/40" />}
                </div>
              ))}
            </div>

            {/* Person name tag */}
            <div className="mt-2 text-sm text-white/80 font-medium">
              📋 Datore di lavoro: <span className="text-white font-bold">{personName}</span>
            </div>
          </div>

          {/* ── Content ─────────────────────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto">
            {step === 1 ? (
              /* ────────── STEP 1 ────────── */
              <div className="p-5 space-y-5">
                {/* Importo reale */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Importo reale ricevuto
                  </label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      inputMode={isMobileViewport ? 'none' : 'decimal'}
                      value={formatMoneyInput(amountDigits)}
                      readOnly={isMobileViewport}
                      onFocus={(e) => {
                        e.stopPropagation();
                        if (isMobileViewport) { e.preventDefault(); setShowMobileKeypad(true); e.target.blur(); }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isMobileViewport) { e.preventDefault(); setShowMobileKeypad(true); e.target.blur(); }
                      }}
                      onChange={(e) => setAmountDigits(extractMoneyDigits(e.target.value))}
                      placeholder="0,00"
                      autoComplete="off"
                      className={`w-full pl-9 pr-4 py-3 border rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                        step1Errors.amount ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                  </div>
                  {step1Errors.amount && (
                    <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {step1Errors.amount}
                    </p>
                  )}
                </div>

                {/* Conto */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    Conto di accredito
                  </label>
                  <CustomSelect
                    value={walletId}
                    onChange={setWalletId}
                    options={walletOptions}
                    placeholder="Seleziona un conto"
                    className="h-12"
                  />
                  {step1Errors.walletId && (
                    <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {step1Errors.walletId}
                    </p>
                  )}
                </div>

                {/* Date range */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Start date */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1 flex items-center justify-between">
                      <span>Inizio periodo</span>
                      <div className="flex items-center gap-0.5">
                        <button type="button" onClick={() => shiftStart(-1)}
                          className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors text-emerald-600">
                          <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" onClick={() => shiftStart(1)}
                          className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors text-emerald-600">
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className={`w-full pl-7 pr-2 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                          step1Errors.startDate ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      />
                    </div>
                    {step1Errors.startDate && (
                      <p className="mt-0.5 text-xs text-red-500">{step1Errors.startDate}</p>
                    )}
                  </div>

                  {/* End date */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1 flex items-center justify-between">
                      <span>Fine periodo</span>
                      <div className="flex items-center gap-0.5">
                        <button type="button" onClick={() => shiftEnd(-1)}
                          className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors text-emerald-600">
                          <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" onClick={() => shiftEnd(1)}
                          className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors text-emerald-600">
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className={`w-full pl-7 pr-2 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                          step1Errors.endDate ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      />
                    </div>
                    {step1Errors.endDate && (
                      <p className="mt-0.5 text-xs text-red-500">{step1Errors.endDate}</p>
                    )}
                  </div>
                </div>

                {/* Preview card: how many transactions found */}
                {startDate && endDate && startDate <= endDate && (
                  <div className={`rounded-xl p-3 flex items-start gap-3 text-sm ${
                    periodTransactions.length > 0
                      ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                      : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                  }`}>
                    <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      periodTransactions.length > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
                    }`} />
                    <div>
                      {periodTransactions.length > 0 ? (
                        <>
                          <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                            {periodTransactions.length} transazion{periodTransactions.length === 1 ? 'e' : 'i'} trovat{periodTransactions.length === 1 ? 'a' : 'e'}
                          </span>
                          <span className="text-emerald-600 dark:text-emerald-400">
                            {' '}nel periodo — totale:{' '}
                            <strong>{formatCurrency(periodTransactions.reduce((s, t) => s + t.amount, 0))}</strong>
                          </span>
                        </>
                      ) : (
                        <span className="text-amber-700 dark:text-amber-300">
                          Nessuna transazione di credito trovata nel periodo selezionato.
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* ────────── STEP 2 ────────── */
              <div className="p-4 space-y-4">
                {/* Summary header */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl p-3 border border-emerald-200 dark:border-emerald-800">
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 font-medium">Importo reale</p>
                      <p className="text-gray-900 dark:text-gray-100 font-bold text-sm mt-0.5">{formatCurrency(realAmount)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 font-medium">Totale selezionato</p>
                      <p className="text-gray-900 dark:text-gray-100 font-bold text-sm mt-0.5">{formatCurrency(selectedTotal)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 font-medium">Coefficiente</p>
                      <p className={`font-bold text-sm mt-0.5 ${
                        coefficient > 1 ? 'text-emerald-600 dark:text-emerald-400' :
                        coefficient < 1 ? 'text-amber-600 dark:text-amber-400' :
                        'text-gray-900 dark:text-gray-100'
                      }`}>{selectedTotal > 0 ? coefficient.toFixed(2) : '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Select all toggle */}
                {periodTransactions.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="flex items-center gap-2 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors"
                  >
                    {selectedIds.size === periodTransactions.length
                      ? <CheckSquare className="w-4 h-4" />
                      : <Square className="w-4 h-4" />
                    }
                    {selectedIds.size === periodTransactions.length ? 'Deseleziona tutto' : 'Seleziona tutto'}
                    <span className="text-gray-400 dark:text-gray-500 font-normal">
                      ({selectedIds.size}/{periodTransactions.length})
                    </span>
                  </button>
                )}

                {/* Transaction list */}
                {periodTransactions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Nessuna transazione di credito trovata nel periodo.</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {periodTransactions.map((tx) => {
                      const isSelected = selectedIds.has(tx.id);
                      const newAmt = rescaledMap[tx.id];
                      return (
                        <button
                          key={tx.id}
                          type="button"
                          onClick={() => toggleTransaction(tx.id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                            isSelected
                              ? 'border-emerald-400 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                              : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 opacity-50'
                          }`}
                        >
                          {/* Checkbox icon */}
                          <div className={`flex-shrink-0 ${isSelected ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}>
                            {isSelected ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                          </div>

                          {/* Date */}
                          <div className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400 w-20">
                            {formatDateLabel(tx.date.split('T')[0])}
                          </div>

                          {/* Description */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {tx.store || tx.note || tx.debtPersonName || '—'}
                            </p>
                          </div>

                          {/* Amounts */}
                          <div className="flex-shrink-0 text-right">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {formatCurrency(tx.amount)}
                            </p>
                            {isSelected && newAmt !== undefined && newAmt !== tx.amount && (
                              <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                                → {formatCurrency(newAmt)}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Info box: closing debt preview */}
                {selectedIds.size > 0 && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3 text-xs text-blue-700 dark:text-blue-300">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold mb-0.5">Cosa succederà:</p>
                        <ul className="space-y-0.5 list-disc list-inside">
                          <li>
                            Le <strong>{selectedIds.size}</strong> transazioni selezionate verranno ricalcolate
                            con coefficiente <strong>{coefficient.toFixed(2)}</strong>
                          </li>
                          <li>
                            Verrà creato un debito di chiusura di <strong>{formatCurrency(realAmount)}</strong> verso <strong>{personName}</strong>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Footer buttons ───────────────────────────────────────────────── */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex gap-3 flex-shrink-0">
            {step === 1 ? (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 text-sm"
                >
                  Annulla
                </button>
                <button
                  type="button"
                  onClick={handleGoToStep2}
                  disabled={periodTransactions.length === 0 && startDate <= endDate}
                  className="flex-[2] flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-medium hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 text-sm shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Avanti — Revisiona Transazioni
                  <ArrowRight className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 flex items-center justify-center gap-1 px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 text-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Indietro
                </button>
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={selectedIds.size === 0}
                  className="flex-[2] flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-medium hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 text-sm shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check className="w-4 h-4" />
                  Applica Stipendio
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Amount Keypad */}
      <MobileAmountKeypad
        isOpen={showMobileKeypad && isMobileViewport}
        value={amountDigits}
        onChange={setAmountDigits}
        onClose={() => setShowMobileKeypad(false)}
        title="Importo stipendio ricevuto"
      />
    </>
  );
};

export default SalaryWizard;
